import { API_URL } from "General/APIEndpoints";
import { projectsDataStore, userInfoStore } from "Stores";
import { observable, action, runInAction, computed, makeObservable } from "mobx";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { API } from "General/API";
import { fileUploadStore } from "../FileUpload/FileUploadStore";
import { download } from "utils/download";
import { fileDetailsStore } from "Components/FileDetails/FileDetailsStore";
import { breadcrumbsStore } from "./BreadcrumbsStore";
import { RouterStore } from "Config/CustomBrowserRouter";
import { foldersStore } from "./FoldersStore";
import { notificationError, notificationSuccess } from "utils/libHelpers/AntdNotification";
import Axios, { CancelTokenSource } from "axios";
import { collectionFolderStore } from "../CollectionFolder/CollectionFolderStore";
import { BrowserStorage } from "utils/BrowserStorage";
import { CollectionFolderFile } from "../CollectionFolder/interfaces";
import { numberSort } from "utils/defaultSort";
import { fileDownloadStore } from "../FileDownload/FileDownloadStore";
import i18n, { I18N_NS } from "Config/i18n";
import { getUrlParams } from "utils/getUrlParams";
import { documentsSearchStore } from "./DocumentsSearchStore";
import { viewsStore } from "Pages/Project/Views/Stores/ViewsStore";
import { findFileById, getDocumentsRowKey, isFolderItem } from "../utils/documentsItems";

export interface DocumentsTableItem
  extends API_FileDataEntity,
    Omit<API_FolderDataEntity, "type">,
    Pick<
      API_ExternalLinkedFolder,
      "jumpToProject" | "linkId" | "parentFolderId" | "parentIsLinked" | "parentProjectId" | "sourceProjectId"
    > {
  _rowKey?: string;
}

export interface DocumentsTableItem extends API_FileDataEntity, Omit<API_FolderDataEntity, "type"> {}

// custom exception that tells that operation should be performed only on array of files, except including folders
export const UNDOWNLOADABLE = "UNDOWNLOADABLE";

// generates unique key for each project of particular user were user can keep his last folder
function getLastFolderIdKey(projectId: number, userId: number) {
  const DOC_POSITION = "DocPos";
  return `${DOC_POSITION}_${projectId}_${userId}`;
}

export const documentsSortByMap: {
  [key in keyof API_FileDataEntity]: API_DOCUMENTSORTING_SORTBY;
} = {
  name: "NAME",
  index: "INDEX",
  description: "DESCRIPTION",
  type: "TYPE",
  size: "SIZE",
  created: "CREATED",
  relations: "RELATION",
};

export interface DocumentsFetchParams {
  limit: number;
  offset?: number;
  sortBy?: API_DOCUMENTSORTING_SORTBY;
  sortOrder?: API_DOCUMENTSORTING_SORTORDER;
  type?: string[];
  relation?: string[];
  showDeleted?: boolean;
  linkId?: number;
}

export interface DocumentsFilters {
  type?: string[];
  relations?: string[];
}

export class DocumentsTableStore extends CoreStore {
  rootFolderId: number = null;
  currentFolderId: number = null;
  currentFolder: DocumentsTableItem = {};
  data: DocumentsTableItem[] = [];
  sortOrder: API_DOCUMENTSORTING_SORTORDER = "ASC";
  sortBy: keyof API_FileDataEntity = "name";
  filters: DocumentsFilters = {};
  fileTypeFilters: string[] = [];
  total: number = 0;
  uplevelFolderId: number = null;
  showDeleted: boolean = false;
  defaultAmount: number = 30;
  private cancelTokenSource: CancelTokenSource = null;
  isDragging: boolean = false;
  folderRelations: API_FolderRelations = {};
  loading: boolean = false;
  /**
   * used to check if folder is linked from other project and evaluate endpoints accordingly
   * */
  _linkedFoldersMap: Map<number, number> = new Map();
  folderRelationsCurrentFolderId: number = null;

  constructor() {
    super();

    makeObservable(this, {
      // observables
      rootFolderId: observable,
      currentFolderId: observable,
      currentFolder: observable,
      data: observable,
      sortOrder: observable,
      sortBy: observable,
      filters: observable,
      fileTypeFilters: observable,
      total: observable,
      uplevelFolderId: observable,
      showDeleted: observable,
      isDragging: observable,
      loading: observable,
      folderRelations: observable,
      // actions
      initData: action.bound,
      fetchDifferentFolder: action.bound,
      createFileLink: action.bound,
      clear: action.bound,
      setDraggable: action.bound,
      fetchList: action.bound,
      fetchSortedBy: action.bound,
      fetchFiltered: action.bound,
      refresh: action.bound,
      loadMoreData: action.bound,
      toggleShowDeleted: action.bound,
      openFileUpload: action.bound,
      downloadFiles: action.bound,
      markFilesAsRead: action.bound,
      setFilesReadStatus: action.bound,
      downloadRelationFile: action.bound,
      downloadFilesPdfAsZip: action.bound,
      downloadFilesPdfWithQRasZip: action.bound,
      openFileDetails: action.bound,
      deleteFiles: action.bound,
      moveFiles: action.bound,
      moveFolder: action.bound,
      goToFolder: action.bound,
      renameCurrentFolder: action.bound,
      renameFolderById: action.bound,
      createFolder: action.bound,
      deleteFolder: action.bound,
      restoreFolder: action.bound,
      getFileById: action.bound,
      getLastFolderId: action.bound,
      getLastFolderLinkId: action.bound,
      isRWMAccessLevel: action.bound,
      restoreFiles: action.bound,
      files: computed,
      folderActions: computed,
      noUpload: computed,
      dndDisabled: computed,
      getIsDragging: computed,
      copyDirectory: action.bound,
      createSymlinkFolder: action.bound,
      clearFilters: action.bound,
    });
  }

  clear() {
    this._preserveCurrentFolder(projectsDataStore.projectId, this.currentFolderId, this.rootFolderId);
    this._linkedFoldersMap = new Map();
    this.data = [];
    this.currentFolderId = undefined;
    this.currentFolder = {};
    this.rootFolderId = undefined;
    this.total = 0;
    this.filters = {};
    this.fileTypeFilters = [];
    this.sortOrder = "ASC";
    this.sortBy = "name";
    this.showDeleted = false;
    this.cancelTokenSource = null;
    this.isDragging = false;
    this.loading = false;
    this.folderRelations = {};
    this.folderRelationsCurrentFolderId = undefined;
  }

  url(id?: number) {
    if (id) {
      return API_URL.DOCUMENTS.getDocumentsByFolderId(projectsDataStore.projectId, id);
    } else {
      return API_URL.DOCUMENTS.getDocumentsOfRootFolder(projectsDataStore.projectId);
    }
  }

  cancelFetch = () => {
    this.cancelTokenSource.cancel();
  };

  processResponse = (folderResponse: API_MultiDocumentResponse) => {
    let data: DocumentsTableItem[] = [];
    if (folderResponse.items.externalFolders?.length) {
      data = data.concat(
        folderResponse.items.externalFolders.map((item) => ({
          id: item.id,
          parentId: item.parentFolderId,
          ...(item as DocumentsTableItem),
        }))
      );
    }
    if (folderResponse.items?.subfolders) {
      data = data.concat(folderResponse.items.subfolders);
    }
    if (folderResponse.items?.files) {
      data = data.concat(folderResponse.items.files);
    }
    if (folderResponse.sorting?.considerPosition) {
      data.sort((a, b) => numberSort(a.responsePosition, b.responsePosition));
    }
    return data.map((item) => ({ ...item, _rowKey: getDocumentsRowKey(item) }));
  };

  setDraggable(param: boolean) {
    this.isDragging = param;
  }

  getLinkIdByFolderId(folderId: number) {
    return this._linkedFoldersMap?.get(folderId);
  }

  setLinkIdByFolderId(folderId: number, linkId: number) {
    this._linkedFoldersMap?.set(folderId, linkId);
  }

  // makes request without changing the store state.
  async fetchList(params: DocumentsFetchParams) {
    try {
      if (this.cancelTokenSource) {
        this.cancelFetch();
      }

      this.loading = true;

      this.cancelTokenSource = Axios.CancelToken.source();
      params.sortBy = documentsSortByMap[this.sortBy];
      params.sortOrder = this.sortOrder;
      params.limit = params.limit || this.defaultAmount;
      if (this.showDeleted) {
        params.showDeleted = true;
      }
      if (this.filters.type) {
        params.type = this.filters.type.slice();
      }
      if (this.filters.relations) {
        params.relation = this.filters.relations.slice();
      }
      let folderResponse: API_MultiDocumentResponse;
      const linkId = this.getLinkIdByFolderId(this.currentFolderId);
      if (linkId) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (params as any).folder = this.currentFolderId;
        const externalFolderResponse = await API.getData<API_ExternalMultiDocumentResponse>(
          API_URL.LINKED.readLinkedFolder(projectsDataStore.projectId, linkId),
          params,
          {
            cancelToken: this.cancelTokenSource.token,
          }
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        folderResponse = externalFolderResponse as any;
      } else {
        folderResponse = await API.getData<API_MultiDocumentResponse>(this.url(+this.currentFolderId), params, {
          cancelToken: this.cancelTokenSource.token,
        });
      }

      runInAction(() => {
        // considering that "parentId" or "parentFolderId" can be absent only in root folder
        if (!folderResponse.items.parentId && !(folderResponse.items as API_ExternalLinkedFolder).parentFolderId) {
          this.rootFolderId = folderResponse.items.id;
        }
        this.cancelTokenSource = null;
        this.loading = false;
      });
      return folderResponse;
    } catch (err) {
      throw err;
    }
  }

  async fetchSortedBy(params: {
    sortOrder: API_DOCUMENTSORTING_SORTORDER;
    sortBy: keyof API_FileDataEntity;
    amount?: number;
    offset?: number;
    linkId?: number;
  }) {
    try {
      this.sortBy = params.sortBy;
      this.sortOrder = params.sortOrder;
      const searchQuery = getUrlParams(window.location.search);
      let linkId: number;
      if (searchQuery.linkId) {
        linkId = +searchQuery.linkId;
      }

      const folderResponse = await this.fetchList({
        limit: params.amount || this.defaultAmount,
        offset: params.offset,
        linkId: params.linkId || linkId,
      });
      runInAction(() => {
        this.currentFolderId = folderResponse.items.id;

        this.uplevelFolderId =
          folderResponse.items.parentId ||
          (folderResponse.items as API_ExternalLinkedFolder).parentFolderId ||
          folderResponse.items.id;
        this.currentFolder = folderResponse.items;
        this.total = folderResponse.total;
        if (folderResponse.items.externalFolders) {
          this.total += folderResponse.items.externalFolders.length;
        }
        this.fileTypeFilters = folderResponse?.filter?.types || [];
        this.data = this.processResponse(folderResponse);
      });
    } catch (error) {
      throw error;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Used to fetch folder and file data for initial load
   */
  @handleStoreState()
  async initData(folderId?: number, linkId?: number) {
    try {
      const lastFolderId = BrowserStorage.get(getLastFolderIdKey(projectsDataStore.projectId, userInfoStore.id));
      const lastLinkId = BrowserStorage.get(
        getLastFolderIdKey(projectsDataStore.projectId, userInfoStore.id) + "_LINK"
      );
      if (!folderId && lastFolderId) {
        runInAction(() => {
          if (lastLinkId) {
            this.setLinkIdByFolderId(lastFolderId, lastLinkId);
          }
          this.currentFolderId = +lastFolderId;
        });
      }
      if (linkId) {
        this.setLinkIdByFolderId(folderId, linkId);
      }

      runInAction(() => {
        this.currentFolderId = folderId || this.rootFolderId;
      });
      await Promise.all([
        this.fetchSortedBy({
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          amount: this.defaultAmount,
        }),
        // foldersStore.fetch(folderId),
        // this tweak is used to get rid of second endpoint call to /api/projects/:id/folders/:id/path
        // which is used for Breadcrumbs and folderStore when you load some inner folder directly,
        // like /project/1/documents/37
        // foldersStore.fetch(folderId),

        foldersStore.fetch(folderId),

        (() => {
          if (!folderId) {
            return breadcrumbsStore.init(folderId);
          } else {
            return Promise.resolve();
          }
        })(),
      ]);
      // here we use the response for foldersStore to init data in breadcrumbsStore
      if (folderId) {
        breadcrumbsStore.init(folderId);
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Used to fetch folder and file data after initial was done.
   * Difference is in using foldersStore.fetchFolder instead of foldersStore.fetch
   * which modifies data instead if resetting it.
   */
  @handleStoreState()
  async fetchDifferentFolder(folderId?: number, linkId?: number) {
    runInAction(() => {
      this.currentFolderId = folderId || this.rootFolderId;
      if (linkId) {
        this.setLinkIdByFolderId(folderId, linkId);
      }
    });
    try {
      await Promise.all([
        this.fetchSortedBy({
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          amount: this.defaultAmount,
        }),
        foldersStore.fetchFolder(this.currentFolderId, false),
        breadcrumbsStore.init(folderId, linkId),
      ]);
    } catch (error) {
      throw error;
    }
  }

  refreshTable = () =>
    this.fetchSortedBy({
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      amount: this.defaultAmount,
    });

  async fetchFiltered(filters: DocumentsFilters) {
    try {
      this.filters = { type: filters.type, relations: filters.relations };
      await this.fetchSortedBy({
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        amount: this.defaultAmount,
      });
    } catch (error) {
      throw error;
    }
  }

  refresh() {
    return Promise.all([
      this.fetchSortedBy({
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        amount: this.defaultAmount,
      }),
      foldersStore.fetchFolder(this.currentFolderId, true),
    ]);
  }

  async loadMoreData(params: { offset: number; limit: number }) {
    try {
      const folderResponse = await this.fetchList({
        offset: params.offset,
        limit: params.limit,
      });
      runInAction(() => {
        const external = folderResponse.items.externalFolders?.length || 0;
        this.total = folderResponse.total + external;
        if (!params.offset) {
          this.data = this.processResponse(folderResponse);
        } else {
          this.data = this.data.concat(this.processResponse(folderResponse));
        }
      });
    } catch (error) {
      throw error;
    }
  }

  async toggleShowDeleted(refresh: boolean = true) {
    this.showDeleted = !this.showDeleted;
    if (refresh) {
      await this.refreshTable();
    }
    return this.showDeleted;
  }

  openFileUpload() {
    fileUploadStore.initModal({ folderId: this.currentFolderId });
  }

  async downloadFiles(fileEntities: (number | API_FileDataEntity)[]) {
    try {
      let files: API_FileDataEntity[];
      let fileIds: number[];
      console.log("downloadFiles fileEntities", fileEntities);
      if (fileEntities.length && fileEntities.every((item) => typeof item === "number")) {
        files = this.getFilesFromIds(fileEntities);
        fileIds = fileEntities.slice();
      } else {
        files = (fileEntities as API_FileDataEntity[]).slice();
        fileIds = fileEntities.map((item) => (item as API_FileDataEntity).id);
      }
      if (
        files.some(
          (item) => !item.fileActions || (!item.fileActions.download && !item.fileActions.downloadWithWatermark)
        )
      ) {
        throw UNDOWNLOADABLE;
      }

      const filesData = this._formatDownloadFormBody(fileEntities);
      fileDownloadStore.submitDownloadJob({
        files: filesData.files,
        withDirectories: false,
        withQR: projectsDataStore.isQRForce(),
      });
      this.markFilesAsRead(fileIds);
    } catch (error) {
      throw error;
    }
  }

  markFilesAsRead(fileIds: number[]) {
    fileIds.forEach((id) => {
      const idx = this.data.findIndex((item) => item.id === id && !isFolderItem(item));
      if (idx !== -1) {
        this.data[idx].unread = false;
      }
    });
  }
  async setFilesReadStatus(fileIds: number[], unread: boolean) {
    const fileData = collectionFolderStore.isVisible
      ? fileIds.map((id) => ({ ...collectionFolderStore.data.find((item) => item.id === id) } as CollectionFolderFile))
      : fileIds.map((id) => ({ ...findFileById(this.data, id) } as API_FileDataEntity));
    fileData.forEach((item, index) => (fileData[index].unread = unread));
    const payload = fileIds.map((id) => ({ id: id, unread: unread }));
    try {
      await API.patchData(API_URL.FILES.updateMultipleFileDetails(projectsDataStore.projectId), payload);
      this.refreshTable();
      if (collectionFolderStore.isVisible) {
        collectionFolderStore.fetch();
      }
    } catch (error) {
      throw error;
    }
  }

  async downloadRelationFile(fileId: number) {
    try {
      const file = await API.getBlob(API_URL.FILES.getFileById(projectsDataStore.projectId, fileId));
      runInAction(() => {
        download(file, file.name);
      });
    } catch (error) {
      throw error;
    }
  }

  _toFileRequest(file: Pick<API_FileDataEntity, "id" | "state"> & { linkId?: number }): API_FileRequest {
    if (file.state === "SYMLINK") {
      return { id: file.linkId, state: file.state };
    }
    if (file.state === "EXTERN_SYMLINK") {
      return { id: file.id, state: file.state, linkId: file.linkId };
    }
    return { id: file.id, state: file.state };
  }

  _formatMoveFileFormBody(filesPayload: Pick<API_FileDataEntity, "id" | "state">[]): API_MultipleFileRequest {
    const files: API_MultipleFileRequest["files"] = filesPayload.map(({ id, state }) => {
      const fileId = +id;
      const matchesFile = (item: Pick<DocumentsTableItem, "id" | "type" | "state">) =>
        item.id === fileId && item.state === state && !isFolderItem(item);
      let foundFile = this.data.find(matchesFile);
      if (!foundFile) {
        foundFile = documentsSearchStore.data.find(matchesFile) || {};
      }

      if (foundFile.state === "SYMLINK") {
        return { id: foundFile.linkId, state: foundFile.state };
      } else if (foundFile.state === "EXTERN_SYMLINK") {
        return { id: foundFile.id, state: foundFile.state, linkId: foundFile.linkId };
      } else {
        return { id: fileId, state: foundFile.state };
      }
    });

    return { files };
  }

  _formatDownloadFormBody(fileEntities: (number | API_FileDataEntity)[]): API_MultipleFileRequest {
    let fileRecords: API_FileDataEntity[];
    if (fileEntities.length && fileEntities.every((item) => typeof item === "number")) {
      fileRecords = fileEntities.map((key) => {
        let foundFile = findFileById(this.data, +key);
        if (!foundFile) {
          foundFile = findFileById(documentsSearchStore.data, +key);
        }
        if (!foundFile) {
          foundFile = findFileById(viewsStore.files, +key) || {};
        }
        return foundFile;
      });
    } else {
      fileRecords = (fileEntities as API_FileDataEntity[]).slice();
    }
    const files: API_MultipleFileRequest["files"] = fileRecords.map((file) => this._toFileRequest(file));

    return { files };
  }

  async downloadFilesPdfAsZip(fileIds: number[]) {
    try {
      const formBody: API_MultipleFileRequest = this._formatDownloadFormBody(fileIds);
      const file: File = await API.getBlobByPost(
        API_URL.FILES.getPdfFilesByIdAsZip(projectsDataStore.projectId),
        formBody
      );
      runInAction(() => {
        download(file, file.name);
        fileIds.forEach((id) => {
          const idx = this.data.findIndex((item) => item.id === id);
          this.data[idx].unread = false;
        });
      });
    } catch (error) {
      if (error.response.data && error.response.data.code !== 1) {
        notificationError(error.response.statusText, error.response.data.message);
      }
      throw error;
    }
  }

  async downloadFilesPdfWithQRasZip(fileIds: number[]) {
    try {
      const formBody: API_DownloadFilesRequests = this._formatDownloadFormBody(fileIds);
      formBody.withQrCode = true;
      const file: File = await API.getBlobByPost(
        API_URL.FILES.getPdfFilesByIdAsZip(projectsDataStore.projectId),
        formBody
      );
      runInAction(() => {
        download(file, file.name);
        fileIds.forEach((id) => {
          const idx = this.data.findIndex((item) => item.id === id);
          this.data[idx].unread = false;
        });
      });
    } catch (error) {
      if (error.response.data && error.response.data.code !== 1) {
        notificationError(error.response.statusText, error.response.data.message);
      }
      throw error;
    }
  }

  async isFreeDocumentPosition(body: API_PositionChangeRequest, parentDirId: number) {
    try {
      return await API.newData<API_BooleanData>(
        API_URL.NUMBERING.checkIfPositionIsFree(projectsDataStore.projectId, parentDirId),
        body
      );
    } catch (error) {
      throw error;
    }
  }

  async changeDocumentPosition(body: API_PositionChangeRequest, parentDirId: number) {
    try {
      await API.newData(API_URL.NUMBERING.changePosition(projectsDataStore.projectId, parentDirId), body);
      this.refresh();
    } catch (error) {
      throw error;
    }
  }

  openFileDetails(fileId: number, from: "dashboard" | "project" | "collection_folder" | "documents", linkId?: number) {
    fileDetailsStore.initModal(this.currentFolderId, fileId, {
      from,
      linkId: linkId,
    });
  }

  async deleteFiles(filesToDelete: API_FileDataEntity[]) {
    try {
      const files: API_FileRequest[] = filesToDelete.map((file) => {
        if (file.state === "SYMLINK") {
          return { id: file.linkId, state: "SYMLINK" };
        } else {
          return { id: file.id, state: file.state };
        }
      });
      const formBody: API_MultipleFileRequest = { files };
      await API.newData(API_URL.FILES.deleteFiles_1(projectsDataStore.projectId), formBody);
      this.refresh();
      if (collectionFolderStore.isVisible) {
        collectionFolderStore.fetch();
      }
    } catch (error) {
      throw error;
    }
  }

  async moveFiles(filesPayload: Pick<API_FileDataEntity, "id" | "state">[], folderId: number) {
    try {
      const formBody: API_MultipleFileRequest = this._formatMoveFileFormBody(filesPayload);
      await API.newData(API_URL.FILES.moveFiles_1(projectsDataStore.projectId, folderId), formBody);
    } catch (error) {
      throw error;
    }
  }

  async createFileLink(files: Pick<API_FileDataEntity, "id" | "state" | "linkId">[], folderId: number) {
    try {
      const formBody: API_MultipleFileRequest = { files: files.map((file) => this._toFileRequest(file)) };
      await API.newData(API_URL.FILES.createSymlink(projectsDataStore.projectId, folderId), formBody);
    } catch (error) {
      throw error;
    }
  }

  async moveFolder(folderToMoveId: number, newParentId: number, stayInCurrentFolder?: boolean) {
    try {
      const body = { id: newParentId };
      await API.editData(API_URL.FOLDERS.moveFolder_1(projectsDataStore.projectId, folderToMoveId), body);
      if (!stayInCurrentFolder) {
        foldersStore.fetch(newParentId);
        breadcrumbsStore.init(folderToMoveId);
      }
    } catch (error) {
      throw error;
    }
  }

  async goToFolder(folderId: number) {
    try {
      const linkId = this.getLinkIdByFolderId(folderId);
      let nextUrl = `/project/${projectsDataStore.projectId}/documents/docs`;
      if (folderId !== this.rootFolderId) {
        nextUrl = `${nextUrl}/${folderId}`;
      }
      if (linkId) {
        nextUrl += `?linkid=${linkId}`;
      }

      // await this.fetch(folderId);
      RouterStore.push(nextUrl);
    } catch (error) {
      throw error;
    }
  }

  async renameCurrentFolder(name: string, description?: string) {
    try {
      await API.editData(API_URL.FOLDERS.renameFolder(projectsDataStore.projectId, this.currentFolderId), {
        name,
        description,
      });
      runInAction(() => {
        this.currentFolder.name = name;
        // also update name in breadcrumbs after successful action
        breadcrumbsStore.init(this.currentFolderId);
        this.refresh();
      });
    } catch (error) {
      throw error;
    }
  }

  async renameFolderById(id: number, name: string, description?: string) {
    try {
      await API.editData(API_URL.FOLDERS.renameFolder(projectsDataStore.projectId, id), { name, description });
      runInAction(() => {
        this.refresh();
      });
    } catch (error) {
      throw error;
    }
  }

  async createFolder(name: string, parentFolderId?: number, description?: string) {
    try {
      const body: API_FolderDataEntity[] = [{ name, description, parentId: parentFolderId || this.currentFolderId }];
      await API.newData(API_URL.FOLDERS.createFolders(projectsDataStore.projectId), body);
      runInAction(() => {
        // also update name in breadcrumbs after successful action
        this.refresh();
      });
    } catch (error) {
      throw error;
    }
  }

  async deleteLink(projectId: number, linkId: number) {
    try {
      await API.deleteData(API_URL.FOLDERS.deleteLinkedFolder(projectId, linkId));
    } catch (error) {
      throw error;
    }
  }
  async deleteLinkInExternProject(data: API_FolderRelationsSymlinkInfo) {
    await this.deleteLink(data.targetProjectId, data.linkId);
    this.getFolderRelations(this.folderRelationsCurrentFolderId);
    this.refresh();
  }

  async deleteFolder(folderId: number, linkId?: number) {
    try {
      if (linkId) {
        await this.deleteLink(projectsDataStore.projectId, linkId);
        this.refresh();
      } else {
        await API.deleteData(API_URL.FOLDERS.deleteFolder_1(projectsDataStore.projectId, folderId));
        runInAction(() => {
          if (this.currentFolderId === folderId) {
            this.goToFolder(this.currentFolder.parentId || this.rootFolderId);
          } else {
            this.refresh();
          }
        });
      }
    } catch (error) {
      throw error;
    }
  }

  async getActiveItemsCountForDelete(projectId: number, folderId: number) {
    try {
      const response = await API.getData<API_SubfolderCounts>(
        API_URL.FOLDERS.getActiveItemsCountForDelete(projectsDataStore.projectId, folderId)
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async restoreFolder(folderId: number) {
    try {
      await API.editData(API_URL.FOLDERS.restoreFolder(projectsDataStore.projectId, folderId), {});
      this.refresh();
    } catch (error) {
      throw error;
    }
  }

  async getFolderRelations(folderId: number) {
    try {
      this.folderRelationsCurrentFolderId = folderId;
      const data = await API.getData<API_FolderRelations>(
        API_URL.FOLDERS.getFolderRelations(projectsDataStore.projectId, folderId)
      );
      runInAction(() => {
        this.folderRelations = data;
      });
    } catch (error) {
      throw error;
    }
  }

  async getFileById(folderId: number, fileId: number) {
    if (folderId === this.currentFolderId) {
      const [file]: API_FileDataEntity[] = this.getFilesFromIds([fileId]);
      if (file) {
        return file;
      }
    }
    try {
      const response = await API.getData(
        API_URL.DOCUMENTS.getDocumentByFileId(projectsDataStore.projectId, folderId, fileId)
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // stores current folderId in session storage to restore it on return to documents module
  _preserveCurrentFolder(projectId: number, currentFolderId: number, rootFolderId: number) {
    const lastFolderIdKey = getLastFolderIdKey(projectId, userInfoStore.id);
    const linkId = this.getLinkIdByFolderId(this.currentFolderId);
    if (currentFolderId !== rootFolderId) {
      BrowserStorage.store(lastFolderIdKey, this.currentFolderId);
      if (linkId) {
        BrowserStorage.store(lastFolderIdKey + "_LINK", linkId);
      } else {
        BrowserStorage.removeStored(lastFolderIdKey + "_LINK");
      }
    } else {
      BrowserStorage.removeStored(lastFolderIdKey);
      if (linkId) {
        BrowserStorage.removeStored(lastFolderIdKey + "_LINK");
      }
    }
  }

  getLastFolderId() {
    const lastFolderId = BrowserStorage.get(getLastFolderIdKey(projectsDataStore.projectId, userInfoStore.id));
    if (lastFolderId) {
      return lastFolderId;
    } else {
      return undefined;
    }
  }
  getLastFolderLinkId() {
    const lastLinkId = BrowserStorage.get(getLastFolderIdKey(projectsDataStore.projectId, userInfoStore.id) + "_LINK");
    if (lastLinkId) {
      return lastLinkId;
    } else {
      return undefined;
    }
  }

  getFilesFromIds(fileIds: number[]) {
    const files: API_FileDataEntity[] = fileIds.reduce((arr, item) => {
      const foundItem = findFileById(this.data, item);
      if (foundItem) {
        arr.push(foundItem);
      } else {
        const foundItem = findFileById(documentsSearchStore.data, item);
        if (foundItem) {
          arr.push(foundItem);
        } else {
          console.warn(`FILE ID "${item}" NOT FOUND`);
        }
      }
      return arr;
    }, []);
    return files;
  }

  isRWMAccessLevel(files: (API_FileDataEntity | CollectionFolderFile)[] = this.files) {
    // RECHT_NURVIEW = "1V";    => View only
    // RECHT_LESEN_WASSERZEICHEN = "1W"; => View with Watermark
    // RECHT_LESEN = "2R"; => Read
    // RECHT_LESEN_UND_SCHREIBEN = "5W"; => Write
    // RECHT_EIGENTUEMER = "9O";    => Owner
    // RECHT_EIGENTUEMER_VERZEICHNIS = "9X"; => Owner and directory
    if (!files) {
      return true;
    }
    return files.some((item) => item.properties?.accessright === "1W");
  }

  async restoreFiles(filesToRestore: API_FileDataEntity[], reason: string) {
    try {
      const files: API_FileRequest[] = filesToRestore.map((file) => {
        return { id: file.id, state: file.state };
      });
      const formBody = { files, reason };
      await API.newData(API_URL.FILES.restoreFile_1(projectsDataStore.projectId), formBody);
      this.refreshTable();
      if (collectionFolderStore.isVisible) {
        collectionFolderStore.fetch();
      }
    } catch (error) {
      throw error;
    }
  }

  async copyDirectory(value: API_FolderCopyRequest) {
    try {
      const response = await API.newData<API_FolderCopyResponse>(
        API_URL.FOLDERS.copyDirectory(projectsDataStore.projectId),
        value
      );

      this.refreshTable();
      if (collectionFolderStore.isVisible) {
        collectionFolderStore.fetch();
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createSymlinkFolder(sourceId: number, targetId: number, newDirName: string) {
    try {
      const formBody: API_FolderSymlink = { linkedDirectoryName: newDirName, targetDirectoryId: targetId };
      await API.newData(API_URL.FOLDERS.createSymlinkForDirectory(projectsDataStore.projectId, sourceId), formBody);
      this.refreshTable();
    } catch (error) {
      throw error;
    }
  }

  async startWorkflowForCurrentFolder(workflow: API_WorkflowListEntry) {
    try {
      const formBody: API_WorkflowStartDefinition = {
        workflowDefinitionId: workflow.workflowdefinition,
        folderId: this.currentFolderId,
      };
      const response = await API.newData<API_ActionInfoRest>(
        API_URL.WORKFLOWS.startWorkflow(projectsDataStore.projectId),
        formBody
      );
      notificationSuccess(
        i18n.t("Success", { ns: I18N_NS.GENERAL }),
        i18n.t("Workflow started", { ns: I18N_NS.WORKFLOW })
      );
      if (response.firstStarterStep === true) {
        RouterStore.push(`/project/${projectsDataStore.projectId}/workflows/assignments/${response.worklistEntryId}`);
      }
    } catch (error) {
      throw error;
    }
  }

  clearFilters() {
    this.filters = {};
    this.fileTypeFilters = [];
    this.sortOrder = "ASC";
    this.sortBy = "name";
    this.showDeleted = false;
    this.folderRelations = {};
    this.folderRelationsCurrentFolderId = undefined;
  }

  get files() {
    return this.data.filter((item) => !isFolderItem(item));
  }

  get folderActions() {
    return this.currentFolder.folderActions || {};
  }

  get folderMenuWorkflows() {
    return this.currentFolder.folderMenueWorkflows;
  }

  get noUpload() {
    return !this.currentFolder.folderActions || !this.currentFolder.folderActions.fileUpload;
  }

  /**
   * DnD is disabled if "AUTOMATICDIRMAPPING" is turned off or user does not have upload access to given folder
   */
  get dndDisabled() {
    if (projectsDataStore.functions.includes("AUTOMATICDIRMAPPING")) {
      return false;
    }
    return this.noUpload;
  }

  get getIsDragging() {
    return this.isDragging;
  }
}

export const documentsTableStore = new DocumentsTableStore();
